import { useContext, useMemo, useRef, useEffect } from "react";
import CheckUpContext from "../../context/check-up";
import Document from "../document";
import Icon from "../icon";
import Question from "../question";
import { useForm } from "react-hook-form";
import useCheckUpUpdateMutation from "../../hooks/mutations/useCheckupUpdateMutation";
import CheckUpDiscardRequestModal from "./check-up-discard-request-modal";
import { getOptionalExamsFromAnswer, getQuestionId } from "../../utils/check-up";
import { getFilenameDetails } from "../../utils/documents";

const CheckUpStep2 = ({ discardModal, setDiscardModal }) => {
  const {
    labels,
    survey,
    currentSectionIndex,
    formData,
    setFormData,
    handlePrevious,
    currentRequest,
  } = useContext(CheckUpContext);

  const section = useMemo(
    () => survey?.definition?.sections[currentSectionIndex],
    [survey, currentSectionIndex]
  );

  const packageQuestionId = useMemo(() => section?.questions?.[0]?.id, [section]);
  const optionalExamsQuestionId = useMemo(() => section?.questions?.[1]?.id, [section]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  const formWatch = watch();
  const initializedFormData = useRef();

  useEffect(() => {
    if (initializedFormData.current) {
      reset(formData);
    }
  }, [reset, formData]);

  useEffect(() => {
    if (!initializedFormData.current && packageQuestionId) {
      initializedFormData.current = true;
      const initializedFields = {
        [packageQuestionId]: currentRequest?.packageSelected?.pack?.id,
      };
      setFormData((current) => ({ ...current, ...initializedFields }));
    }
  }, [setFormData, packageQuestionId, currentRequest]);

  const optionalExamsQuestion = useMemo(
    () => ({
      id: optionalExamsQuestionId,
      label: "question.label.21",
      maxNAnswers: currentRequest?.packageSelected?.pack?.maxOptional,
      minNAnswers: 0,
      position: 1,
      questionType: "Custom-checkbox", //currentRequest?.packageSelected?.pack?.maxOptional === 1 ? "Multiple choice" : "Checkboxes"
      status: "PUBLISHED",
      answers: currentRequest?.packageSelected?.pack?.optionals?.map((exam, i) => ({
        id: exam.id,
        label: exam.name,
        nextSectionType: "NEXT_SECTION",
        position: i,
      })),
    }),
    [currentRequest, optionalExamsQuestionId]
  );

  const { mutateAsync: updateRequest, isError: isUpdateError } = useCheckUpUpdateMutation();

  const onSubmit = async (values) => {
    const packageSelected = {
      optionalsExams: getOptionalExamsFromAnswer(values, optionalExamsQuestionId),
      package: currentRequest?.packageSelected?.pack?.id,
    };
    await updateRequest({
      survey,
      request: currentRequest,
      formData,
      values,
      packageSelected,
      email: formData[getQuestionId(survey, "mail")],
      phone: formData[getQuestionId(survey, "phone")],
      gender: formData[getQuestionId(survey, "gender")],
      dateOfBirth: formData[getQuestionId(survey, "birthdate")],
      workplace: formData[getQuestionId(survey, "workPlace")],
      birthPlace: formData[getQuestionId(survey, "birthPlace")],
      taxCode: formData[getQuestionId(survey, "fiscalCode")],
      preferredTimeSlot: formData[getQuestionId(survey, "availability")],
      quadro: values[getQuestionId(survey, "quadro")],
      manager: values[getQuestionId(survey, "manager")],
      lastCompletedStep: 1,
    });
  };

  const examFile = useMemo(() => {
    const file = currentRequest?.packageSelected?.pack?.examFile;
    return file ? { ...file, ...getFilenameDetails(file?.fileName) } : null;
  }, [currentRequest]);

  return (
    <>
      <div className="app-content__list app-content__text">
        {currentRequest?.packageSelected?.pack?.exams?.map(
          (cat) =>
            cat?.category && (
              <div key={cat.category}>
                <p>
                  {labels?.[`WELFARE_CHECKUP_CATEGORY_${cat.category.toUpperCase()}`] ||
                    cat.category}
                </p>
                <ul>
                  {cat.exams?.map((exam) => (
                    <li key={exam.id}>
                      {labels?.[`WELFARE_CHECKUP_EXAM_${exam.name.replace(/ /g, "-")}`] ||
                        exam.name}
                    </li>
                  ))}
                </ul>
              </div>
            )
        )}
      </div>
      {examFile && <Document doc={examFile} removable={false} />}
      <div
        className="app-content__text"
        dangerouslySetInnerHTML={{
          __html: (currentRequest?.packageSelected?.pack?.maxOptional > 1
            ? labels?.WELFARE_CHECKUP_SELECT_OPTIONAL_EXAMS ||
              `<strong>A cui puoi aggiungere [N] visite specialistiche a scelta tra:</strong>`
            : labels?.WELFARE_CHECKUP_SELECT_OPTIONAL_EXAM ||
              `<strong>A cui puoi aggiungere 1 visita specialistica a scelta tra:</strong>`
          ).replace("[N]", currentRequest?.packageSelected?.pack?.maxOptional),
        }}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="form check-up-content__checkboxes"
        key={section?.id}
        noValidate
      >
        <input type="hidden" {...register(`${packageQuestionId}`)} />
        <Question
          question={optionalExamsQuestion}
          register={register}
          error={errors?.[`${optionalExamsQuestionId}`]}
          touched={touchedFields?.[`${optionalExamsQuestionId}`]}
          isDirty={dirtyFields?.[`${optionalExamsQuestionId}`]}
          control={control}
          trigger={trigger}
          labels={labels}
          value={formWatch?.[`${optionalExamsQuestionId}`]}
          maxNAnswers={optionalExamsQuestion.maxNAnswers} //optionalExamsQuestion.maxNAnswers === 1 ? false : optionalExamsQuestion.maxNAnswers
          isInline={true}
        />
        <div className="form__buttons-wrapper form__buttons-wrapper--static">
          {isUpdateError && (
            <p className="form__error text-center">
              {labels?.WELFARE_BORSE_ERROR_SAVING_REQUEST || "Error saving requests"}
            </p>
          )}
          <div className="form__buttons form__buttons--space-between">
            <button className="link" onClick={handlePrevious} type="button">
              <Icon name="angle-down" />
              {labels?.WELFARE_FORM_BTN_PREVIOUS || "Previous"}
            </button>
            <button className="button primary form__submit" onClick={handleSubmit}>
              {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
            </button>
          </div>
        </div>
      </form>
      <CheckUpDiscardRequestModal
        key={!!discardModal}
        discardModal={discardModal}
        setDiscardModal={setDiscardModal}
        formWatch={formWatch}
        optionalsExams={formWatch?.[`${optionalExamsQuestionId}`]}
        lastCompletedStep={0}
      />
    </>
  );
};

export default CheckUpStep2;
